<template>
  <Collapse
    class="green-operation__assessment green-operation__lighting-system"
  >
    <template #title>
      <HKLLeftRight>
        <span class="font-bold">{{ $t('tenancy.greenOperation.assessments.lightingSystem.title') }}</span>
        <HKLInputPoints
          v-model="points"
          :isEdit="false"
          v-if="!isEdit && canViewPoint"
        />
      </HKLLeftRight>
    </template>
    <template #content>
      <div>
        <HKLLeftRight class="green-operation__assessment-title">
          <span class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.lightingSystem.controlStrategiesInstalled') }}
          </span>
          <HKLInputPoints
            :isEdit="isEdit"
            v-model="controlStrategiesInstalled.point"
            v-if="canViewPoint"
          />
        </HKLLeftRight>
        <div class="green-operation__assessment-form row">
          <template v-if="isTenant">
            {{ controlStrategiesInstalledHint }}
          </template>
          <template v-else>
            <HKLCheckbox
              v-for="(option, index) of optionMap.controlStrategiesInstalled"
              :key="index"
              :disabled="!isEdit"
              class="small-4"
              v-model="controlStrategiesInstalled.value[option.key]"
            >
              {{ option.label }}
            </HKLCheckbox>
          </template>
        </div>
      </div>
      <HKLTable
        v-bind="tableAttrs"
        v-if="tableAttrs.dataSource.length > 0"
      />
    </template>
  </Collapse>
</template>

<script>
import { toRefs, reactive, computed, unref } from 'vue-demi'
import Collapse from '@/controls/Collapse.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLCheckbox from '@/controls/Button/HKLCheckbox.vue'
import { useStoreGetters } from '@/store/index'
import i18n from '@/utils/i18n'
import HKLInputPoints from '../components/HKLInputPoints.vue'
import { useGreenOperationField, useGreenOperationAssessmentFormStatus } from '../hook'
import { GREEN_OPERATION_ASSESSMENT_TYPE } from '../consts'
import { useAssessmentDocumentTableAttrs, useAssessmentForm } from './hook'
import { totalPoints } from './utils'
import { useGreenOperationPerms } from '../GreenOperationLetter.hook'

const ASSESSMENT_TYPE = GREEN_OPERATION_ASSESSMENT_TYPE.LIGHTING

export default {
  components: {
    Collapse,
    HKLTable,
    HKLLeftRight,
    HKLCheckbox,
    HKLInputPoints,
  },
  setup () {
    const sourceId = useGreenOperationField('id', '')
    const { isEdit } = useGreenOperationAssessmentFormStatus()
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const { canViewPoint } = useGreenOperationPerms()
    const tableAttrs = useAssessmentDocumentTableAttrs(sourceId, ASSESSMENT_TYPE)
    const optionMap = computed(() => ({
      controlStrategiesInstalled: [
        {
          label: i18n.t('tenancy.greenOperation.assessments.lightingSystem.occupancySensor'),
          key: 'occupancySensor',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.lightingSystem.zoneControl'),
          key: 'zoneControl',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.lightingSystem.timerControl'),
          key: 'timerControl',
        },
      ],
    }))
    const formMap = reactive({
      controlStrategiesInstalled: useAssessmentForm(
        ASSESSMENT_TYPE,
        'controlStrategiesInstalled',
        {
          occupancySensor: false,
          zoneControl: false,
          timerControl: false,
        },
      ),
      controlStrategiesInstalledHint: computed(() => {
        const valueMap = unref(formMap.controlStrategiesInstalled).value
        const list = unref(optionMap).controlStrategiesInstalled
          .filter(({ key }) => Boolean(valueMap[key]))
        return list.length > 0
          ? list.map(({ label }) => label).join(', ')
          : i18n.t('emptyValue')
      }),
    })
    return {
      isEdit,
      isTenant,
      canViewPoint,
      tableAttrs,
      ...toRefs(formMap),
      optionMap,
      points: computed(() => totalPoints(formMap)),
    }
  },
}
</script>

<style lang="scss" scoped>
@import './common.scss';
</style>
